import React, { useCallback, useMemo, useRef, useState } from "react";
import { runMatchRate } from "../../utils/matchRate";
import { HistoryWindow } from "./history";
import { ScheduledJobRun } from "./types";

// bd-deepcontext-hfv7u.6 — minimap strip over the full fetched run history.
// One thin bar per run (match-rate height, status color), with a draggable /
// clickable window overlay showing which slice the chart above is displaying.
// Clicking or dragging the overlay re-centres the window; the parent owns the
// window state and the lazy-load of older pages, so this component only emits a
// desired window start index via `onWindowStartChange`.
export interface JobRunsMinimapProps {
  // Full chronological (oldest → newest) run history loaded so far.
  runs: ScheduledJobRun[];
  window: HistoryWindow;
  onWindowStartChange: (start: number) => void;
  // bd-hfv7u.10 — optional date-range label (e.g. "Jun 24 – Jun 30") shown in
  // the strip's right caption alongside the run count.
  rangeLabel?: string;
}

const HEIGHT = 36;
const STATUS_COLOR: Record<ScheduledJobRun["status"], string> = {
  completed: "#16a34a",
  failed: "#dc2626",
  running: "#2563eb",
  pending: "#9ca3af",
};

const JobRunsMinimap: React.FC<JobRunsMinimapProps> = ({
  runs,
  window: win,
  onWindowStartChange,
  rangeLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const total = runs.length;
  const windowSize = win.end - win.start;

  // Geometry in percentages so the strip scales with its container width.
  const barWidthPct = total > 0 ? 100 / total : 0;
  const overlayLeftPct = total > 0 ? (win.start / total) * 100 : 0;
  const overlayWidthPct = total > 0 ? (windowSize / total) * 100 : 0;

  // Translate a client X position into a window start index (centre the window
  // on the click/drag point), clamped by the parent's clampWindow.
  const startFromClientX = useCallback(
    (clientX: number): number => {
      const el = containerRef.current;
      if (!el || total <= 0) return 0;
      const rect = el.getBoundingClientRect();
      const ratio = (clientX - rect.left) / rect.width;
      const centreIdx = Math.round(ratio * total);
      return centreIdx - Math.floor(windowSize / 2);
    },
    [total, windowSize],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (total <= 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
      onWindowStartChange(startFromClientX(e.clientX));
    },
    [total, startFromClientX, onWindowStartChange],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      onWindowStartChange(startFromClientX(e.clientX));
    },
    [dragging, startFromClientX, onWindowStartChange],
  );

  const endDrag = useCallback(() => setDragging(false), []);

  const bars = useMemo(
    () =>
      runs.map((run, i) => {
        const rate = runMatchRate(run);
        const h = Math.max(2, Math.round(rate * HEIGHT));
        return { id: run.id, i, h, color: STATUS_COLOR[run.status] };
      }),
    [runs],
  );

  if (total === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-ink-muted">
          Full history — click to jump
        </span>
        <span className="text-xs text-ink-muted">
          {rangeLabel ? `${rangeLabel} · ` : ""}
          {total} runs
        </span>
      </div>
      <div
        ref={containerRef}
        role="slider"
        aria-label="History window"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, total - windowSize)}
        aria-valuenow={win.start}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            onWindowStartChange(win.start - 1);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            onWindowStartChange(win.start + 1);
          }
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        className="relative w-full rounded bg-ghost-bg border border-line cursor-pointer select-none"
        style={{ height: HEIGHT + 8 }}
        title="Click or drag to move the visible window"
      >
        {/* Per-run bars, baseline-anchored. */}
        <div
          className="absolute inset-x-0 bottom-0 flex items-end"
          style={{ height: HEIGHT, padding: "0 1px" }}
        >
          {bars.map((b) => (
            <div
              key={b.id}
              style={{
                width: `${barWidthPct}%`,
                height: b.h,
                backgroundColor: b.color,
                opacity: 0.7,
                marginRight: total > 80 ? 0 : 1,
              }}
            />
          ))}
        </div>
        {/* Draggable window overlay. */}
        <div
          className="absolute top-0 bottom-0 border-2 border-accent bg-accent bg-opacity-10 rounded pointer-events-none"
          style={{
            left: `${overlayLeftPct}%`,
            width: `${Math.max(overlayWidthPct, 2)}%`,
          }}
        />
      </div>
    </div>
  );
};

export default JobRunsMinimap;
